<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Statement of Profit or Loss and Other Comprehensive Income
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2 pl-2">
                  <v-col md="3" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col md="3" cols="12">
                    <v-date-input
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="12" cols="12">
                    <div class="d-flex justify-start align-center flex-wrap">
                      <v-btn class="bg-primary mb-4" type="submit" @click="loadIncomeStatement" elevation="2" >
                        <v-icon left>mdi-magnify</v-icon> Search
                      </v-btn>
                    </div>
                  </v-col>
                
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="incomeStatementData" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!incomeStatementData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!incomeStatementData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Income Statement Report -->
                <v-card v-if="incomeStatementData" class="mt-2">
                  <v-card-title class="text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">STATEMENT OF PROFIT OR LOSS AND OTHER COMPREHENSIVE INCOME</div>
                      <div class="text-subtitle-1">FOR THE PERIOD ENDED {{ formattedDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <!-- Income Statement Table - Exact Image Design -->
                    <div class="income-statement-exact">
                      <!-- Header -->
                      <div class="income-header">
                        <div class="header-particulars">Particulars</div>
                        <div class="header-note">Note</div>
                        <div class="header-amounts">Amount in Taka</div>
                      </div>
                      <div class="income-subheader">
                        <div class="header-particulars"></div>
                        <div class="header-note"></div>
                        <div class="header-amount-box">{{ formattedDate(filters.to_date) }}</div>
                        <div class="header-amount-box">{{ formatPreviousDate(filters.to_date) }}</div>
                      </div>

                      <!-- Revenue Section -->
                      <div class="income-row">
                        <div class="particulars-text section-header">REVENUE</div>
                        <div class="note-box"></div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Sales Revenue -->
                      <template v-if="incomeStatementData.revenues">
                        <div v-for="(item, index) in incomeStatementData.revenues" :key="'revenue-' + index" class="income-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="note-box"></div>
                          <div class="amount-box">{{ formatAmount(item.current_balance) }}</div>
                          <div class="amount-box">{{ formatAmount(item.previous_balance) }}</div>
                        </div>
                      </template>

                      <!-- Net Sales Total -->
                      <div class="income-row total-row" v-if="incomeStatementData.net_sales_total">
                        <div class="particulars-text total-text">Net Sales</div>
                        <div class="note-box"></div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.net_sales_total.current_balance) }}</div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.net_sales_total.previous_balance) }}</div>
                      </div>

                      <!-- Direct Expenses Section -->
                      <div class="income-row">
                        <div class="particulars-text section-header">DIRECT EXPENSES</div>
                        <div class="note-box"></div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Cost of Goods Sold -->
                      <template v-if="incomeStatementData.direct_expenses">
                        <div v-for="(item, index) in incomeStatementData.direct_expenses" :key="'direct-' + index" class="income-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="note-box"></div>
                          <div class="amount-box">{{ formatAmount(item.current_balance) }}</div>
                          <div class="amount-box">{{ formatAmount(item.previous_balance) }}</div>
                        </div>
                      </template>

                      <!-- Gross Profit -->
                      <div class="income-row total-row" v-if="incomeStatementData.gross_profit_total">
                        <div class="particulars-text total-text">Gross Profit/(Loss)</div>
                        <div class="note-box"></div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.gross_profit_total.current_balance) }}</div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.gross_profit_total.previous_balance) }}</div>
                      </div>

                      <!-- Indirect Expenses Section -->
                      <div class="income-row">
                        <div class="particulars-text section-header">INDIRECT EXPENSES</div>
                        <div class="note-box"></div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <!-- Administrative Expenses -->
                      <template v-if="incomeStatementData.indirect_expenses">
                        <div v-for="(item, index) in incomeStatementData.indirect_expenses" :key="'indirect-' + index" class="income-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="note-box"></div>
                          <div class="amount-box">{{ formatAmount(item.current_balance) }}</div>
                          <div class="amount-box">{{ formatAmount(item.previous_balance) }}</div>
                        </div>
                      </template>

                      <!-- Operating Profit -->
                      <div class="income-row total-row" v-if="incomeStatementData.operating_profit_total">
                        <div class="particulars-text total-text">Operating Profit/(Loss)</div>
                        <div class="note-box"></div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.operating_profit_total.current_balance) }}</div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.operating_profit_total.previous_balance) }}</div>
                      </div>

                      <!-- Financial Expenses Section -->
                      <div class="income-row">
                        <div class="particulars-text section-header">FINANCIAL EXPENSE</div>
                        <div class="note-box"></div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <template v-if="incomeStatementData.financial_expenses">
                        <div v-for="(item, index) in incomeStatementData.financial_expenses" :key="'financial-' + index" class="income-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="note-box"></div>
                          <div class="amount-box">{{ formatAmount(item.current_balance) }}</div>
                          <div class="amount-box">{{ formatAmount(item.previous_balance) }}</div>
                        </div>
                      </template>

                      <!-- Other Income Section -->
                      <div class="income-row">
                        <div class="particulars-text section-header">OTHER INCOME</div>
                        <div class="note-box"></div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <template v-if="incomeStatementData.other_incomes">
                        <div v-for="(item, index) in incomeStatementData.other_incomes" :key="'other-' + index" class="income-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="note-box"></div>
                          <div class="amount-box">{{ formatAmount(item.current_balance) }}</div>
                          <div class="amount-box">{{ formatAmount(item.previous_balance) }}</div>
                        </div>
                      </template>

                      <!-- Net Profit Before Tax -->
                      <div class="income-row total-row" v-if="incomeStatementData.net_profit_before_tax">
                        <div class="particulars-text total-text">Net Profit/(Loss) before Tax</div>
                        <div class="note-box"></div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.net_profit_before_tax.current_balance) }}</div>
                        <div class="amount-box total-box">{{ formatAmount(incomeStatementData.net_profit_before_tax.previous_balance) }}</div>
                      </div>

                      <!-- Income Tax Expense Section -->
                      <div class="income-row">
                        <div class="particulars-text section-header">INCOME TAX EXPENSE</div>
                        <div class="note-box"></div>
                        <div class="amount-box-empty"></div>
                        <div class="amount-box-empty"></div>
                      </div>

                      <template v-if="incomeStatementData.income_tax_expenses">
                        <div v-for="(item, index) in incomeStatementData.income_tax_expenses" :key="'tax-' + index" class="income-row">
                          <div class="particulars-text indent">{{ item.name }}</div>
                          <div class="note-box"></div>
                          <div class="amount-box">{{ formatAmount(item.current_balance) }}</div>
                          <div class="amount-box">{{ formatAmount(item.previous_balance) }}</div>
                        </div>
                      </template>

                      <!-- Net Profit After Tax -->
                      <div class="income-row grand-total-row" v-if="incomeStatementData.net_profit_after_tax">
                        <div class="particulars-text grand-total-text">Net Profit/(Loss) After Tax Transferred to BS</div>
                        <div class="note-box"></div>
                        <div class="amount-box grand-total-box">{{ formatAmount(incomeStatementData.net_profit_after_tax.current_balance) }}</div>
                        <div class="amount-box grand-total-box">{{ formatAmount(incomeStatementData.net_profit_after_tax.previous_balance) }}</div>
                      </div>
                    </div>

                  </v-card-text>
                </v-card>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <p class="mt-4 text-h6">Generating Income Statement...</p>
                  </v-card-text>
                </v-card>

                <!-- Error State -->
                <v-card v-if="error" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-icon color="error" size="64">mdi-alert-circle</v-icon>
                    <p class="mt-4 text-h6 text-error">{{ error }}</p>
                    <v-btn color="primary" @click="loadIncomeStatement" class="mt-2">
                      Try Again
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import formattedDate from '@/plugins/dateFormatter'
import { ref, onMounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex';

const store = useStore()

const instance = getCurrentInstance()

const panel = ref([0])
const loading = ref(false)
const error = ref(null)
const incomeStatementData = ref(null)

const filters = ref({
  from_date: null,
  to_date: null,
})

const loadIncomeStatement = async () => {
  loading.value = true
  error.value = null
  
  try {
    const params = {
      from_date: formattedDate(filters.value.from_date),
      to_date: formattedDate(filters.value.to_date),
    }
    
    const response = await store.dispatch('getIncomeStatement', params)
    
    if (response.success) {
      incomeStatementData.value = response.data
    } else {
      error.value = response.message || 'Failed to load income statement'
    }
  } catch (err) {
    console.error('Error loading income statement:', err)
    error.value = err.response?.data?.message || 'Failed to load income statement'
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    from_date: null,
    to_date: null,
  }
  incomeStatementData.value = null
  error.value = null
}

const formatPreviousDate = (date) => {
  if (!date) return ''
  try {
    const d = new Date(date)
    const previousYear = new Date(d.getFullYear() - 1, d.getMonth(), d.getDate())
    return previousYear.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return ''
  }
}

const formatAmount = (amount) => {
  if (!amount) return '0.00'
  
  // Extract numeric value from strings like "1,500,000.00 Dr" or "800,000.00 Cr"
  const numericValue = parseFloat(amount.replace(/[^\d.-]/g, ''))
  
  if (isNaN(numericValue)) return '0.00'
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericValue)
}

const exportToExcel = () => {
  if (!incomeStatementData.value) return
  
  const data = [
    ['STATEMENT OF PROFIT OR LOSS AND OTHER COMPREHENSIVE INCOME'],
    [`For the period ended ${formattedDate(filters.value.to_date)}`],
    [],
    ['Particulars', 'Note', `Amount (${formattedDate(filters.value.to_date)})`, `Amount (${formatPreviousDate(filters.value.to_date)})`],
    []
  ]

  // REVENUE SECTION
  data.push(['REVENUE', '', '', ''])
  
  if (incomeStatementData.value.revenues) {
    incomeStatementData.value.revenues.forEach((item) => {
      data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.net_sales_total) {
    data.push(['Net Sales', '', incomeStatementData.value.net_sales_total.current_balance || '0.00', incomeStatementData.value.net_sales_total.previous_balance || '0.00'])
  }
  
  data.push([])
  
  // DIRECT EXPENSES SECTION
  data.push(['DIRECT EXPENSES', '', '', ''])
  
  if (incomeStatementData.value.direct_expenses) {
    incomeStatementData.value.direct_expenses.forEach((item) => {
      data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.gross_profit_total) {
    data.push(['Gross Profit/(Loss)', '', incomeStatementData.value.gross_profit_total.current_balance || '0.00', incomeStatementData.value.gross_profit_total.previous_balance || '0.00'])
  }
  
  data.push([])
  
  // INDIRECT EXPENSES SECTION
  data.push(['INDIRECT EXPENSES', '', '', ''])
  
  if (incomeStatementData.value.indirect_expenses) {
    incomeStatementData.value.indirect_expenses.forEach((item) => {
      data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.operating_profit_total) {
    data.push(['Operating Profit/(Loss)', '', incomeStatementData.value.operating_profit_total.current_balance || '0.00', incomeStatementData.value.operating_profit_total.previous_balance || '0.00'])
  }
  
  data.push([])
  
  // FINANCIAL EXPENSES SECTION
  data.push(['FINANCIAL EXPENSE', '', '', ''])
  
  if (incomeStatementData.value.financial_expenses) {
    incomeStatementData.value.financial_expenses.forEach((item) => {
      data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  data.push([])
  
  // OTHER INCOME SECTION
  data.push(['OTHER INCOME', '', '', ''])
  
  if (incomeStatementData.value.other_incomes) {
    incomeStatementData.value.other_incomes.forEach((item) => {
      data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.net_profit_before_tax) {
    data.push(['Net Profit/(Loss) before Tax', '', incomeStatementData.value.net_profit_before_tax.current_balance || '0.00', incomeStatementData.value.net_profit_before_tax.previous_balance || '0.00'])
  }
  
  data.push([])
  
  // INCOME TAX EXPENSE SECTION
  data.push(['INCOME TAX EXPENSE', '', '', ''])
  
  if (incomeStatementData.value.income_tax_expenses) {
    incomeStatementData.value.income_tax_expenses.forEach((item) => {
      data.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  data.push([])
  
  // NET PROFIT AFTER TAX
  if (incomeStatementData.value.net_profit_after_tax) {
    data.push(['Net Profit/(Loss) After Tax Transferred to BS', '', incomeStatementData.value.net_profit_after_tax.current_balance || '0.00', incomeStatementData.value.net_profit_after_tax.previous_balance || '0.00'])
  }

  instance.proxy.$exportTableToExcel(data, 'income_statement_' + instance.proxy.$fileDate())
}

const exportToPDF = () => {
  if (!incomeStatementData.value) return
  
  const columns = ['Particulars', 'Note', `Current (${formattedDate(filters.value.to_date)})`, `Previous (${formatPreviousDate(filters.value.to_date)})`]
  const rows = []

  // REVENUE SECTION
  rows.push(['REVENUE', '', '', ''])
  
  if (incomeStatementData.value.revenues) {
    incomeStatementData.value.revenues.forEach((item) => {
      rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.net_sales_total) {
    rows.push(['Net Sales', '', incomeStatementData.value.net_sales_total.current_balance || '0.00', incomeStatementData.value.net_sales_total.previous_balance || '0.00'])
  }
  
  rows.push(['', '', '', ''])
  
  // DIRECT EXPENSES SECTION
  rows.push(['DIRECT EXPENSES', '', '', ''])
  
  if (incomeStatementData.value.direct_expenses) {
    incomeStatementData.value.direct_expenses.forEach((item) => {
      rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.gross_profit_total) {
    rows.push(['Gross Profit/(Loss)', '', incomeStatementData.value.gross_profit_total.current_balance || '0.00', incomeStatementData.value.gross_profit_total.previous_balance || '0.00'])
  }
  
  rows.push(['', '', '', ''])
  
  // INDIRECT EXPENSES SECTION
  rows.push(['INDIRECT EXPENSES', '', '', ''])
  
  if (incomeStatementData.value.indirect_expenses) {
    incomeStatementData.value.indirect_expenses.forEach((item) => {
      rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.operating_profit_total) {
    rows.push(['Operating Profit/(Loss)', '', incomeStatementData.value.operating_profit_total.current_balance || '0.00', incomeStatementData.value.operating_profit_total.previous_balance || '0.00'])
  }
  
  rows.push(['', '', '', ''])
  
  // FINANCIAL EXPENSES SECTION
  rows.push(['FINANCIAL EXPENSE', '', '', ''])
  
  if (incomeStatementData.value.financial_expenses) {
    incomeStatementData.value.financial_expenses.forEach((item) => {
      rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  rows.push(['', '', '', ''])
  
  // OTHER INCOME SECTION
  rows.push(['OTHER INCOME', '', '', ''])
  
  if (incomeStatementData.value.other_incomes) {
    incomeStatementData.value.other_incomes.forEach((item) => {
      rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  if (incomeStatementData.value.net_profit_before_tax) {
    rows.push(['Net Profit/(Loss) before Tax', '', incomeStatementData.value.net_profit_before_tax.current_balance || '0.00', incomeStatementData.value.net_profit_before_tax.previous_balance || '0.00'])
  }
  
  rows.push(['', '', '', ''])
  
  // INCOME TAX EXPENSE SECTION
  rows.push(['INCOME TAX EXPENSE', '', '', ''])
  
  if (incomeStatementData.value.income_tax_expenses) {
    incomeStatementData.value.income_tax_expenses.forEach((item) => {
      rows.push([`  ${item.name}`, '', item.current_balance || '0.00', item.previous_balance || '0.00'])
    })
  }
  
  rows.push(['', '', '', ''])
  
  // NET PROFIT AFTER TAX
  if (incomeStatementData.value.net_profit_after_tax) {
    rows.push(['Net Profit/(Loss) After Tax Transferred to BS', '', incomeStatementData.value.net_profit_after_tax.current_balance || '0.00', incomeStatementData.value.net_profit_after_tax.previous_balance || '0.00'])
  }

  instance.proxy.$exportTableToPDF(
    columns,
    rows,
    `STATEMENT OF PROFIT OR LOSS AND OTHER COMPREHENSIVE INCOME - For the period ended ${formattedDate(filters.value.to_date)}`,
    'income_statement_' + instance.proxy.$fileDate(),
    'portrait',
    {
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fontSize: 9, fillColor: [66, 139, 202], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 80 },  // Particulars
        1: { cellWidth: 20 },  // Notes
        2: { cellWidth: 45, halign: 'right' },  // Current Period
        3: { cellWidth: 45, halign: 'right' }   // Previous Period
      },
      margin: { top: 40 }
    }
  )
}

onMounted(() => {
  // Set default dates (current year)
  const today = new Date()
  const yearStart = new Date(today.getFullYear(), 0, 1)
  
  filters.value.from_date = yearStart.toISOString().split('T')[0]
  filters.value.to_date = today.toISOString().split('T')[0]
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

/* Exact Image Design - Individual Rectangular Boxes */
.income-statement-exact {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  background-color: white;
}

/* Header Styles - Clean, No Borders */
.income-header {
  display: flex;
  align-items: center;
  font-weight: bold;
  min-height: 40px;
  padding: 8px 0;
}

.income-subheader {
  display: flex;
  align-items: center;
  font-weight: bold;
  min-height: 40px;
  padding: 8px 0;
}

.header-particulars {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
}

.header-note {
  flex: 0 0 60px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
}

.header-amounts {
  flex: 0 0 300px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
}

.header-amount-box {
  width: 120px;
  height: 30px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  margin: 3px 12px;
  border-radius: 0;
  box-shadow: none;
  text-align: center;
  flex-shrink: 0;
}

/* Row Styles - Clean, No Borders */
.income-row {
  display: flex;
  align-items: center;
  min-height: 35px;
  padding: 6px 0;
}

/* Particulars Text */
.particulars-text {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
  background-color: white;
  font-weight: normal;
}

.indent {
  padding-left: 24px;
  font-size: 13px;
}

/* Section Headers - Bold */
.section-header {
  font-weight: bold;
}

.total-text {
  font-weight: bold;
}

.grand-total-text {
  font-weight: bold;
}

/* Note Box */
.note-box {
  flex: 0 0 60px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  background-color: white;
}

/* Individual Amount Boxes - No borders */
.amount-box {
  width: 120px;
  height: 30px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: normal;
  margin: 3px 12px;
  border-radius: 0;
  box-shadow: none;
  text-align: center;
  flex-shrink: 0;
}

/* Empty boxes for section headers */
.amount-box-empty {
  width: 120px;
  height: 30px;
  background-color: white;
  margin: 3px 12px;
  flex-shrink: 0;
}

/* Special Row Styles */
.total-row {
  border-top: 1px solid #000;
  padding-top: 8px;
  margin-top: 6px;
}

.grand-total-row {
  border-top: 1px solid #000;
  border-bottom: 3px solid #000; /* Double underline for grand total */
  padding: 8px 0 6px 0;
  margin-top: 6px;
}

/* Special Box Styles */
.total-box {
  font-weight: bold;
}

.grand-total-box {
  font-weight: bold;
}

/* Responsive Design */
@media (max-width: 768px) {
  .income-statement-exact {
    font-size: 12px;
  }
  
  .header-amounts {
    flex: 0 0 240px;
  }
  
  .header-amount-box {
    width: 100px;
    height: 26px;
    font-size: 11px;
    margin: 3px 8px;
  }
  
  .amount-box {
    width: 100px;
    height: 26px;
    font-size: 11px;
    margin: 3px 8px;
  }
  
  .amount-box-empty {
    width: 100px;
    height: 26px;
    margin: 3px 8px;
  }
  
  .header-particulars,
  .particulars-text {
    padding: 6px 8px;
    font-size: 12px;
  }
  
  .note-box {
    flex: 0 0 50px;
    padding: 6px 8px;
    font-size: 12px;
  }
}

/* Print Styles */
@media print {
  .income-statement-exact {
    font-size: 11px;
  }
  
  .header-amounts {
    flex: 0 0 260px;
  }
  
  .header-amount-box {
    width: 110px;
    height: 28px;
    font-size: 10px;
    margin: 3px 10px;
  }
  
  .amount-box {
    width: 110px;
    height: 28px;
    font-size: 10px;
    margin: 3px 10px;
  }
  
  .amount-box-empty {
    width: 110px;
    height: 28px;
    margin: 3px 10px;
  }
  
  .header-particulars,
  .particulars-text {
    padding: 4px 6px;
    font-size: 11px;
  }
  
  .note-box {
    flex: 0 0 50px;
    padding: 4px 6px;
    font-size: 11px;
  }
}

/* Hover Effects */
.income-row:hover .particulars-text {
  background-color: #f8f9fa;
}

.income-row:hover .amount-box {
  background-color: #f8f9fa;
}

.income-subheader:hover .header-amount-box {
  background-color: #f8f9fa;
}

/* Ensure exact alignment like image */
.income-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.particulars-text {
  flex: 1;
  min-width: 0;
}

.header-amount-box,
.amount-box,
.amount-box-empty {
  flex: 0 0 120px;
  margin: 3px 12px;
}

@media (max-width: 768px) {
  .header-amount-box,
  .amount-box,
  .amount-box-empty {
    flex: 0 0 100px;
    margin: 3px 8px;
  }
}

@media print {
  .header-amount-box,
  .amount-box,
  .amount-box-empty {
    flex: 0 0 110px;
    margin: 3px 10px;
  }
}
</style>
